import React, { useEffect, useState } from 'react';
import { FontIcon, getTheme, IStyle, mergeStyles, mergeStyleSets } from '@fluentui/react';
import { BwjPillSize, BwjPillSizes } from './pill-sizes';

export interface IBwjPillProps {
  content: any;
  toContentString?: (content: any) => string;
  onRenderContent?: (content: any) => JSX.Element;
  size?: BwjPillSize;
  onPillClick?: (content: any) => void;
  onActionButtonClick?: (content: any) => void;
  actionButtonIcon?: string;
  disabled?: boolean;
}

const contentContainerClassName = mergeStyles([{
  padding: 0,
  margin: 0,
  flexGrow: 1,
}]);

export const BwjPill: React.FunctionComponent<IBwjPillProps> = (props) => {

  const { size, onPillClick, disabled, onActionButtonClick } = props;
  const [theme] = useState(getTheme());
  const [styleSet, setStyleSet] = useState<{pill: string, content: string, actionButton: string}>({pill: '', content: '', actionButton: ''});

  const pillRef = React.createRef<HTMLDivElement>();

  useEffect(() => {
    const pillSize = size ?? BwjPillSizes.regular;

    const pillStyle: IStyle[] = [{
      height: pillSize * 2,
      border: 'none',
      borderRadius: pillSize,
      margin: 0,
      marginRight: pillSize / 2,
      marginBottom: pillSize / 2,
      padding: 0,
      paddingLeft: pillSize,
      display: 'flex',
      flexDirection: 'row',
      boxSizing: 'border-box',
    }];
    if(!disabled) {
      pillStyle.push({
        backgroundColor: theme.palette.themeSecondary,
      });
    } else {
      pillStyle.push({
        backgroundColor: theme.palette.neutralTertiary,
      });
    }
    !onActionButtonClick && pillStyle.push({
      paddingRight: pillSize,
    });
    !disabled && !!onPillClick && pillStyle.push({
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.themePrimary,
      },
    });

    const contentStyle: IStyle[] = [{
      padding: 0,
      margin: 0,
      fontSize: pillSize,
      color: theme.palette.neutralLighter,
      width: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      lineHeight: pillSize * 2,
      textAlign: 'center',
      boxSizing: 'border-box',
      cursor: 'default',
    }];
    !!onActionButtonClick && contentStyle.push({
      paddingRight: pillSize / 2,
    });
    !disabled && !!onPillClick && contentStyle.push({
      cursor: 'pointer',
    });

    const actionButtonStyle: IStyle[] = [{
      width: pillSize * 2,
      height: pillSize * 2,
      border: 'none',
      fontSize: pillSize,
      color: theme.palette.neutralLighter,
      borderRadius: pillSize,
      backgroundColor: 'transparent',
      lineHeight: pillSize * 2,
      textAlign: 'center',
    }];
    if(!disabled) {
      actionButtonStyle.push({
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.themeDark,
        },
      });
    }

    setStyleSet(mergeStyleSets({
      pill: pillStyle,
      content: contentStyle,
      actionButton: actionButtonStyle
    }));
  }, [theme, size, onPillClick, onActionButtonClick, disabled]);

  const getContentElement = () => {
    if(!!props.onRenderContent) {
      return props.onRenderContent(props.content);
    } else {
      const text = !!props.toContentString ? props.toContentString(props.content) : '' + props.content;
      return (<p className={styleSet.content}>{text}</p>);
    }
  };

  const handlePillClick = (e: React.MouseEvent<HTMLDivElement>) => {
    !props.disabled && e.currentTarget === pillRef.current && !!props.onPillClick &&
    props.onPillClick(props.content);
  };

  const handleActionButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    !props.disabled && !!onActionButtonClick && onActionButtonClick(props.content);
  };

    return (
      <div className={styleSet.pill} onClick={handlePillClick} ref={pillRef}>
        <div className={contentContainerClassName}>
          {getContentElement()}
        </div>
        {!!onActionButtonClick && <button className={styleSet.actionButton} onClick={handleActionButtonClick}>
          <FontIcon iconName={props.actionButtonIcon ?? 'Cancel'} aria-hidden="true" />
        </button>}
      </div>
    );
};
